import { Injectable } from '@angular/core';
import { interval, Observable, Subscription } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { EventType } from '../model/chat-enums';
import { UUIDGenerator } from '../model/uuid-generator.gen';
import { ChatMessage, RoomAssigned } from '../model/chat.message';
import { ChatService } from './chat.service';
import { GET_CHAT_SOCKET_ENV_URL } from 'src/app/constants/constants';
import { environment } from 'src/environments/environment';
import { ChatUserTyping, statusSocketResponse } from '../model/user-interfaces';

@Injectable({
  providedIn: 'root'
})
export class SocketService {
  private socket: Socket;
  private subscriptions: Subscription[] = [];

  constructor(private chatService: ChatService) { }

  ngOnInit(): void { }


  public async initSocket() {
    try {
      const origin = location.origin.toLowerCase();
      const wsChatUrl = GET_CHAT_SOCKET_ENV_URL();

      let deviceId = localStorage.getItem('deviceId')?.trim();
      if (!deviceId) {
        deviceId = UUIDGenerator.generateUUID();
        localStorage.setItem('deviceId', deviceId);
      }

      this.socket = io(wsChatUrl, {
        forceNew: false,
        reconnection: true,
        autoConnect: true,
        reconnectionAttempts: 500,
        transports: ['websocket', 'polling', 'webtransport'],
        auth: {
          Authorization: await this.getToken(),
          platform: "web",
          deviceId: deviceId
        },
      });
    } catch (error) {
    }
    finally {
      console.log('socket', this.socket);
      return !!this.socket?.connected;
    }
  }


  disconnectEvent() {
    this.subscriptions.push(
      this.onEvent(EventType.DISCONNECT)
        .pipe()
        .subscribe(async (disconnect: any) => {
          console.log('disconnect event:--', disconnect);
          if (disconnect == 'io server disconnect') {
            await this.closeSocket();
            await this.connectSocket();
          }
        })
    );
  }

  reconnectErrorEvent() {
    this.subscriptions.push(
      this.onEvent(EventType.RECONNECT_ERROR)
        .pipe()
        .subscribe((reconnect: any) => {
          console.log('reconnectErrorEvent reconnect event:--', reconnect);
        })
    );
  }

  async getToken() {
    let token = localStorage.getItem(environment.X_Auth_Token);
    return token;
  }

  public setupPingInterval(): void {
    this.subscriptions.push(
      interval(10000).subscribe(() => {
        this.sendPing();
        console.log('IO client socket ping......');
      }));
  }

  public connect(): void {
    this.socket?.connect();
  }

  public sendTyping(event: EventType.TYPING, typing: ChatUserTyping) {
    this.socket?.emit(event, typing);
  }

  public onEvent(event: EventType): Observable<any> {
    return new Observable<any>((observer) => {
      this.socket?.on(event, (status: any) => {
        observer.next(status);
      });
    });
  }

  public offEvent(event: EventType) {
    if (this.socket?.hasListeners(event)) {
      this.socket?.removeAllListeners(event);
    }
  }

  public disconnect(): void {
    this.socket?.disconnect();
  }

  isSocketConnected() {
    return this.socket?.connected;
  }

  public sendPing() {
    this.socket?.emit(EventType.PING);
  }

  connectEvent() {
    this.subscriptions.push(
      this.onEvent(EventType.CONNECT)
        .pipe()
        .subscribe((status: any) => {
          console.log('connect event: socket--', this.socket);
        }));
  }

  async connectSocket(): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        await this.initSocket();
        if (!this.socket?.connected) {
          resolve(false);
        }
        this.connect();
        this.setupPingInterval();
        this.subscriptions.push(
          this.onEvent(EventType.ERROR).subscribe(async (error: any) => {
            if (error?.isLogIn == false && error?.response?.statusCode !== 400) {

            }
            if (error?.response?.statusCode === 400) {
              await this.closeSocket();
              this.connectSocket();
            }
          }));

        this.disconnectEvent();
        this.connectEvent();
        this.createMessage();
        this.typeEvent();
        this.removeUnassignedRoom();
        this.closeChatRoom();
        this.onlineStatus();
        this.mentionAgent();
      } catch (error) {
        console.log('error', error);
      }
      finally {
        resolve(true)
      }
    });
  }

  closeSocket(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      try {
        for (const sub of this.subscriptions) {
          sub?.unsubscribe();
        }
        this.socket?.disconnect();
      } catch (error) {
        console.log('error', error);
      }
      finally {
        resolve(true)
      }
    });
  }

  createMessage() {
    this.subscriptions.push(
      this.onEvent(EventType.CREATE_MESSAGE)
        .pipe()
        .subscribe((message: ChatMessage) => {
          this.chatService.setMessages(message);
        }));
  }

  typeEvent() {
    this.subscriptions.push(
      this.onEvent(EventType.TYPING)
        .pipe()
        .subscribe((typing: ChatUserTyping) => {
          this.chatService.setUserTyping(typing);
        }));
  }

  public async sendMessage(event: EventType.CREATE_MESSAGE, message: any) {
    if (!this.socket?.connected) {
      await this.closeSocket();
      await this.connectSocket();
    }
    this.socket?.emit(event, message);
  }

  removeUnassignedRoom() {
    this.subscriptions.push(
      this.onEvent(EventType.REMOVE_ROOM_AFTER_ASSIGN)
        .pipe()
        .subscribe((message: RoomAssigned) => {
          this.chatService.setUnassignedRoom(message);
        }));
  }

  closeChatRoom() {
    this.subscriptions.push(
      this.onEvent(EventType.CLOSE_ROOM)
        .pipe()
        .subscribe((message: any) => {
          this.chatService.setClosedChatRoom(message);
        }));
  }

  onlineStatus() {
    this.subscriptions.push(
      this.onEvent(EventType.UPDATE_MEMBER_STATUS)
        .pipe()
        .subscribe((message: statusSocketResponse) => {
          this.chatService.setOnlineStatus(message);
        }));
  }

  mentionAgent() {
    this.subscriptions.push(
      this.onEvent(EventType.MENTIONS_AGENT)
        .pipe()
        .subscribe((message: any) => {
          this.chatService.setMentionAgent(message);
        }));
  }
}
